import { useCallback, useContext } from "react";
import { createContext, ReactNode } from "react";
import jwt_decode, { JwtPayload } from "jwt-decode";
import axios from "axios";

interface SessionProviderProps {
  children: ReactNode;
}

interface JwtProps {
  userId: string;
  email: string;
  exp: number;
  role: string[];
}

interface UserProps {
  id: string;
  email: string;
  expires: boolean;
  role: string[];
}

interface SessionContextProps {
  signIn: (token: string) => void;
  signOut: () => void;
  getSessionAuthorization: (onlyToken?: boolean) => {};
  getUser: () => UserProps | undefined;
}

const SessionContext = createContext({} as SessionContextProps);

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const getUser = (token?: string): any => {
    const localToken = token ? token : localStorage.getItem("backoffice@confi");

    if (localToken) {
      let dataJwt;
      try {
        jwt_decode<JwtPayload>(localToken, { header: true });
        const jwtDecoded = jwt_decode<JwtProps>(localToken);

        dataJwt = {
          id: jwtDecoded.userId,
          email: jwtDecoded.email,
          expires: jwtDecoded.exp < (new Date().getTime() + 1) / 1000,
          role: jwtDecoded.role,
        };
      } catch (error: any) {
        signOut();
        window.location.replace("/");
      }
      return dataJwt;
    }
  };

  const getSessionAuthorization = useCallback((onlyToken?: boolean) => {
    const cancelRequest = axios.CancelToken.source();
    const token = localStorage.getItem("backoffice@confi") || "";

    if (onlyToken) {
      return token.length > 0 ? token : "";
    }

    return token.length > 0
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          },
          cancelToken: cancelRequest.token,
        }
      : "";
  }, []);

  const signIn = useCallback((token: string) => {
    localStorage.removeItem("backoffice@confi");
    localStorage.setItem("backoffice@confi", token);
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem("backoffice@confi");
  }, []);

  return (
    <SessionContext.Provider
      value={{
        signIn,
        signOut,
        getSessionAuthorization,
        getUser,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext);
